import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner } from '../../../shared/components';
import { ApiError } from '../../../shared/services/apiClient';
import {
  getWhatsAppChannelAccountSettings,
  getWhatsAppSubscribedApps,
  registerWhatsAppPhoneNumber,
  subscribeWhatsAppApp,
} from '../services/channelAccountService';
import {
  completeWhatsAppCloudSignup,
  startWhatsAppCloudSignup,
} from '../services/whatsAppCloudSignupService';
import {
  ChannelAccountWhatsAppSettings,
  WhatsAppPhoneNumberRegistration,
  WhatsAppSubscribedApps,
} from '../types/channelAccount';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import { ChannelAccountBar } from './ChannelAccountBar';
import { ConnectorFieldStatus } from './ConnectorFieldStatus';
import {
  launchWhatsAppEmbeddedSignup,
  WhatsAppEmbeddedSignupCancelled,
} from '../utils/whatsAppEmbeddedSignup';

const NOT_CONFIGURED_CODE = 'WHATSAPP_SIGNUP_NOT_CONFIGURED';

const getWebhookUrl = (): string => {
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  const origin = apiBase || window.location.origin.replace(/\/$/, '');
  return `${origin}/api/v1/whatsapp/cloud/webhook`;
};

const formatDateTime = (value?: string): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

interface WhatsAppCloudConnectorTabProps {
  canManage: boolean;
}

export const WhatsAppCloudConnectorTab: React.FC<WhatsAppCloudConnectorTabProps> = ({ canManage }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();
  const whatsappAccounts = useChannelAccounts('WhatsApp', canManage, 'cloud');

  const [platformConfigured, setPlatformConfigured] = React.useState<boolean | null>(null);
  const [connecting, setConnecting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const [loadingSettings, setLoadingSettings] = React.useState(false);
  const [accountSettings, setAccountSettings] = React.useState<ChannelAccountWhatsAppSettings | null>(null);
  const [subscribedApps, setSubscribedApps] = React.useState<WhatsAppSubscribedApps | null>(null);
  const [loadingSubscribedApps, setLoadingSubscribedApps] = React.useState(false);
  const [subscribing, setSubscribing] = React.useState(false);
  const [registerPin, setRegisterPin] = React.useState('');
  const [registering, setRegistering] = React.useState(false);
  const [latestRegistration, setLatestRegistration] = React.useState<WhatsAppPhoneNumberRegistration | null>(null);

  const webhookUrl = React.useMemo(getWebhookUrl, []);

  // The session endpoint doubles as the availability probe: it is the only thing that knows whether
  // the operator has filled in the platform's Meta credentials.
  React.useEffect(() => {
    if (!canManage) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        await startWhatsAppCloudSignup();
        if (!cancelled) {
          setPlatformConfigured(true);
        }
      } catch (probeError) {
        if (cancelled) {
          return;
        }

        if (probeError instanceof ApiError && probeError.code === NOT_CONFIGURED_CODE) {
          setPlatformConfigured(false);
          return;
        }

        setPlatformConfigured(true);
        setError(probeError instanceof Error ? probeError.message : t('failedToConnectWhatsAppCloud'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canManage, t]);

  const refreshSubscribedApps = React.useCallback(async (accountId: string) => {
    setLoadingSubscribedApps(true);
    try {
      setSubscribedApps(await getWhatsAppSubscribedApps(accountId));
    } catch {
      setSubscribedApps(null);
    } finally {
      setLoadingSubscribedApps(false);
    }
  }, []);

  const loadAccountSettings = React.useCallback(async (accountId: string) => {
    setLoadingSettings(true);
    setError(null);

    try {
      const data = await getWhatsAppChannelAccountSettings(accountId);
      setAccountSettings(data);
      setLatestRegistration(null);
      setRegisterPin('');

      if (data.hasCloudAccessToken && data.whatsAppAccountId) {
        await refreshSubscribedApps(accountId);
      } else {
        setSubscribedApps(null);
      }
    } catch (loadError) {
      setAccountSettings(null);
      setError(loadError instanceof Error ? loadError.message : t('failedToLoadWhatsAppSettings'));
    } finally {
      setLoadingSettings(false);
    }
  }, [refreshSubscribedApps, t]);

  React.useEffect(() => {
    const accountId = whatsappAccounts.selectedAccountId;
    if (!accountId) {
      setAccountSettings(null);
      setSubscribedApps(null);
      return;
    }

    void loadAccountSettings(accountId);
  }, [loadAccountSettings, whatsappAccounts.selectedAccountId]);

  const connectWithMeta = async () => {
    setConnecting(true);
    setError(null);
    setNotice(null);

    try {
      // The signed state expires, so it is minted at click time rather than reused from the probe.
      const session = await startWhatsAppCloudSignup();
      const signup = await launchWhatsAppEmbeddedSignup(session);

      const result = await completeWhatsAppCloudSignup({
        code: signup.code,
        state: session.state,
        whatsAppAccountId: signup.whatsAppAccountId,
        phoneNumberId: signup.phoneNumberId ?? undefined,
      });

      await whatsappAccounts.reload();
      whatsappAccounts.setSelectedAccountId(result.channelAccountId);

      const message = result.created
        ? t('whatsappCloudConnected', { name: result.phoneNumber || result.displayName })
        : t('whatsappCloudReconnected', { name: result.phoneNumber || result.displayName });
      setNotice(message);
      showToast(message, 'success');

      if (!result.appSubscribed) {
        setError(t('whatsappCloudSubscribeWarning'));
      }
    } catch (connectError) {
      if (connectError instanceof WhatsAppEmbeddedSignupCancelled) {
        setNotice(t('whatsappCloudCancelled'));
        return;
      }

      if (connectError instanceof ApiError && connectError.code === NOT_CONFIGURED_CODE) {
        setPlatformConfigured(false);
        return;
      }

      const message = connectError instanceof Error ? connectError.message : t('failedToConnectWhatsAppCloud');
      setError(message);
      showToast(message, 'error');
    } finally {
      setConnecting(false);
    }
  };

  const registerNumber = async () => {
    const accountId = whatsappAccounts.selectedAccountId;
    if (!accountId) {
      return;
    }

    const pin = registerPin.trim();
    if (!/^\d{6}$/.test(pin)) {
      const message = t('whatsappRegisterPinRequired');
      setError(message);
      setNotice(null);
      showToast(message, 'error');
      return;
    }

    setRegistering(true);
    setError(null);
    setNotice(null);

    try {
      const result = await registerWhatsAppPhoneNumber(accountId, { pin });
      setLatestRegistration(result);
      const message = t('whatsappNumberRegistered', { cloudPhoneNumberId: result.cloudPhoneNumberId });
      setNotice(message);
      showToast(message, 'success');
    } catch (registerError) {
      const message = registerError instanceof Error ? registerError.message : t('failedToRegisterWhatsAppNumber');
      setError(message);
      showToast(message, 'error');
    } finally {
      setRegistering(false);
    }
  };

  const subscribeApp = async () => {
    const accountId = whatsappAccounts.selectedAccountId;
    if (!accountId) {
      return;
    }

    setSubscribing(true);
    setError(null);
    setNotice(null);

    try {
      const result = await subscribeWhatsAppApp(accountId);
      const message = t('whatsappAppSubscribed', { whatsAppAccountId: result.whatsAppAccountId });
      setNotice(message);
      showToast(message, 'success');
      await refreshSubscribedApps(accountId);
    } catch (subscribeError) {
      const message = subscribeError instanceof Error ? subscribeError.message : t('failedToSubscribeWhatsAppApp');
      setError(message);
      showToast(message, 'error');
    } finally {
      setSubscribing(false);
    }
  };

  const copyWebhookUrl = () => {
    void navigator.clipboard.writeText(webhookUrl);
    showToast(t('whatsappCloudWebhookCopied'), 'success');
  };

  if (!canManage) {
    return null;
  }

  const renderConnectedAccount = () => {
    if (loadingSettings) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <Spinner text={t('loadingWhatsAppSettings')} />
        </div>
      );
    }

    if (!accountSettings) {
      return null;
    }

    return (
      <>
        <div className="telegram-status-grid" style={{ marginTop: 16 }}>
          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('whatsappCloudStatusAccount')}</div>
            <dl className="telegram-status-list">
              <div>
                <dt>{t('whatsappPhoneNumber')}</dt>
                <dd>{accountSettings.phoneNumber || t('telegramStatusNotAvailable')}</dd>
              </div>
              <div>
                <dt>{t('whatsappStatusCloudPhoneNumberId')}</dt>
                <dd>{accountSettings.cloudPhoneNumberId || t('telegramStatusNotAvailable')}</dd>
              </div>
              <div>
                <dt>{t('whatsappStatusAccountId')}</dt>
                <dd>{accountSettings.whatsAppAccountId || t('telegramStatusNotAvailable')}</dd>
              </div>
              <div>
                <dt>{t('whatsappStatusAccessToken')}</dt>
                <dd><ConnectorFieldStatus done={accountSettings.hasCloudAccessToken} /></dd>
              </div>
            </dl>
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('whatsappStatusWebhook')}</div>
            <dl className="telegram-status-list">
              <div>
                <dt>{t('whatsappStatusWebhookUrl')}</dt>
                <dd>{webhookUrl}</dd>
              </div>
              <div>
                <dt>{t('whatsappStatusVerifyToken')}</dt>
                <dd><ConnectorFieldStatus done={accountSettings.hasCloudWebhookVerifyToken} /></dd>
              </div>
              <div>
                <dt>{t('whatsappStatusSignatureValidation')}</dt>
                <dd><ConnectorFieldStatus done={accountSettings.validateCloudSignature} variant="enabled" /></dd>
              </div>
            </dl>
            <div className="company-actions-inline" style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-secondary" onClick={copyWebhookUrl}>
                {t('whatsappCloudWebhookCopy')}
              </button>
            </div>
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('whatsappStatusSubscribedApps')}</div>
            {loadingSubscribedApps ? (
              <div className="telegram-status-empty">{t('loadingWhatsAppSubscribedApps')}</div>
            ) : subscribedApps?.items.length ? (
              <dl className="telegram-status-list">
                {subscribedApps.items.map((app, index) => (
                  <div key={app.appId || `${index}`}>
                    <dt>{app.appName || t('telegramStatusNotAvailable')}</dt>
                    <dd>{app.appId || t('telegramStatusNotAvailable')}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <div className="telegram-status-empty">{t('whatsappStatusNoSubscribedApps')}</div>
            )}
            <div className="company-actions-inline" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={subscribeApp}
                disabled={subscribing || !accountSettings.hasCloudAccessToken || !accountSettings.whatsAppAccountId}
              >
                {subscribing ? t('savingEllipsis', { ns: 'admin' }) : t('subscribeWhatsAppApp')}
              </button>
            </div>
          </div>
        </div>

        <div className="company-section" style={{ marginTop: 16 }}>
          <div className="company-section-head">
            <h3>{t('whatsappCloudRegisterHeading')}</h3>
          </div>
          <p style={{ color: '#516173', marginTop: 0 }}>{t('whatsappCloudRegisterDescription')}</p>

          <div className="company-form-grid">
            <label>
              {t('whatsappRegisterPin')}
              <input
                className="text-input"
                value={registerPin}
                onChange={event => setRegisterPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                placeholder="000000"
              />
            </label>
          </div>

          {latestRegistration ? (
            <div style={{ color: '#516173', fontSize: 13, marginTop: 8 }}>
              {t('whatsappStatusRegisteredAt')}: {formatDateTime(latestRegistration.registeredAtUtc)}
            </div>
          ) : null}

          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={registerNumber}
              disabled={registering || !accountSettings.hasCloudAccessToken || !accountSettings.cloudPhoneNumberId}
            >
              {registering ? t('savingEllipsis', { ns: 'admin' }) : t('registerWhatsAppNumber')}
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <section className="company-section">
      <div className="company-section-head">
        <h3>{t('whatsappCloudHeading')}</h3>
      </div>

      <p style={{ color: '#516173', marginTop: 0 }}>{t('whatsappCloudDescription')}</p>

      {error ? <div className="error">{error}</div> : null}
      {notice ? <div className="success">{notice}</div> : null}

      {platformConfigured === null ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <Spinner text={t('loadingWhatsAppSettings')} />
        </div>
      ) : platformConfigured === false ? (
        <div className="telegram-status-card" style={{ marginTop: 12 }}>
          <div className="telegram-status-card-title">{t('whatsappCloudNotConfigured')}</div>
          <div className="telegram-status-empty">{t('whatsappCloudNotConfiguredHelp')}</div>
        </div>
      ) : (
        <>
          <ol style={{ color: '#516173', marginTop: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
            <li>{t('whatsappCloudStepConnect')}</li>
            <li>{t('whatsappCloudStepChoose')}</li>
            <li>{t('whatsappCloudStepRegister')}</li>
          </ol>

          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={connectWithMeta}
              disabled={connecting}
            >
              {connecting
                ? t('whatsappCloudConnecting')
                : whatsappAccounts.accounts.length > 0
                  ? t('whatsappCloudConnectAnother')
                  : t('whatsappCloudConnect')}
            </button>
          </div>

          {whatsappAccounts.accounts.length === 0 ? (
            <p style={{ color: '#516173', marginTop: 16 }}>{t('whatsappCloudNoAccounts')}</p>
          ) : (
            <>
              <ChannelAccountBar
                channel="WhatsApp"
                connectionKind="cloud"
                accounts={whatsappAccounts.accounts}
                limits={whatsappAccounts.limits}
                loading={whatsappAccounts.loading}
                error={whatsappAccounts.error}
                selectedAccountId={whatsappAccounts.selectedAccountId}
                onSelectAccount={whatsappAccounts.setSelectedAccountId}
                onRefresh={whatsappAccounts.reload}
                canManage={canManage}
                showAddForm={false}
                onToggleAddForm={connectWithMeta}
                onAccountUpdated={() => {
                  const accountId = whatsappAccounts.selectedAccountId;
                  if (accountId) {
                    void loadAccountSettings(accountId);
                  }
                }}
              />

              {renderConnectedAccount()}
            </>
          )}
        </>
      )}
    </section>
  );
};
